import { Card, CardBody, CardHeader, CardTitle, Alert } from '@/common'
import { useAppConfig } from '@/shared/hooks/api/appConfig'
import { AddAssetWithIdPayload } from '../useAssetInstances'
import { ProductionRouteAsset } from '@/features/production-routes/api/ProductionRouteModel'
import { useTranslation } from 'react-i18next'
import AssetCategoryEditor from '../components/AssetCategoryEditor'

export function CombustionOfFuelAssetEditor({
  selectedAssets,
  addAssetWithId,
  removeAssetWithId,
  updateAssetWithId,
  id,
  removeMultipleAssets,
  isIronOrSteelProduct = false
}: {
  selectedAssets: ProductionRouteAsset[]
  addAssetWithId: (payload: AddAssetWithIdPayload) => void
  removeAssetWithId: (assetId: number) => void
  updateAssetWithId: (assetId: number, asset: ProductionRouteAsset) => void
  id: string
  removeMultipleAssets: (ids: number[]) => void
  isIronOrSteelProduct?: boolean
}) {
  const { t } = useTranslation()
  const { assetsById } = useAppConfig().config

  const combustionOfFuelAssetDefinitions = [
    assetsById['batch_annealing_furnace.box_furnace'],
    assetsById['continuous_annealing_furnaces'],
    assetsById['belltype_annealing_furnace'],
    assetsById['vacuum_annealing_furnace'],
    assetsById['other_annealing_furnace'],
    assetsById['preheating_furnace'],
    assetsById['holding_furnace'],
    assetsById['casting_machine'],
    assetsById['tension_leveler'],
    assetsById['melting_furnace'],
    assetsById['pure_stretching_machine'],
    assetsById['pre_treatment_line'],
    assetsById['melting_holding_furnace'],
    assetsById['vertical_pusher_furnace'],
    assetsById['cleaning_line'],
    assetsById['dual_chamber_furnace'],
    assetsById['surface_treatment_line'],
    assetsById['continuous_annealing_line'],
    assetsById['pre_treatment_line_preheating_furnace'],
    assetsById['continuous_heat_treatment_line']
  ]

  const furnaceAssetDefinitions = [...combustionOfFuelAssetDefinitions]

  const castingStageAssetDefinitions = [
    assetsById['continuous_casting_machine'],
    assetsById['ingot_casting']
  ]

  const rollingStageAssetDefinitions = [
    assetsById['hot_rolled_mill'],
    assetsById['cold_rolled_mill'],
    assetsById['bars_and_rod_mill'],
    assetsById['wire_mill'],
    assetsById['beams_billets_rails_and_tube_mill']
  ]

  const heatTreatmentAndCleaningAssetDefinitions = [
    assetsById['annealing_furnace'],
    assetsById['pickling_machine_pickling_line']
  ]

  const surfaceAndProtectiveFinishingAssetDefinitions = [
    assetsById['galvanising_line'],
    assetsById['coating_line'],
    assetsById['painting_line'],
    assetsById['finishing_machine']
  ]

  const cuttingAndShapingAssetDefinitions = [
    assetsById['cut_to_length_machine'],
    assetsById['slitting_line'],
    assetsById['stamping_machine'],
    assetsById['forging_press'],
    assetsById['welding_machine']
  ]

  const assetDefinitions = isIronOrSteelProduct
    ? [
        ...castingStageAssetDefinitions,
        ...rollingStageAssetDefinitions,
        ...heatTreatmentAndCleaningAssetDefinitions,
        ...surfaceAndProtectiveFinishingAssetDefinitions,
        ...cuttingAndShapingAssetDefinitions
      ]
    : furnaceAssetDefinitions

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle title={t(`emissionPoints:combustion_of_fuel.title`)} />
        </CardHeader>
        <CardBody className="space-y-4">
          <Alert type="info">{t(`emissionPoints:combustion_of_fuel.callout`)}</Alert>
          <AssetCategoryEditor
            selectedAssets={selectedAssets}
            addAssetWithId={addAssetWithId}
            removeAssetWithId={removeAssetWithId}
            updateAssetWithId={updateAssetWithId}
            removeMultipleAssets={removeMultipleAssets}
            id={id}
            assetDefinitions={assetDefinitions}
            title={t(`emissionPoints:combustion_of_fuel.assetCategories.combustion_of_fuel.title`)}
            technologyTypeQuestion={t(
              `emissionPoints:combustion_of_fuel.assetCategories.combustion_of_fuel.technologyTypeQuestion`
            )}
          />
        </CardBody>
      </Card>
    </>
  )
}
